import * as React from 'react';
import { useEffect, useState } from 'react';
import { SelectWidget } from '../../../../widgets/select/SelectWidget';
import { useSystem } from '../../../../hooks/useSystem';
import { IntegrationModel, OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { styled } from '../../../../theme';
import { GithubAccountType, IntegrationKind } from '@journeyapps-platform/types-cerberus';
import * as _ from 'lodash';

export interface GithubReposDropdownWidgetProps {
  integration: IntegrationModel;
  org: OrganizationModel;
  setProperties: (props: { owner?: string; repo?: string; create?: boolean }) => any;
  repo?: string;
  appName: string;
  groupingName: string;
}

export function filterGitHubRepoName(name?: string): string | undefined {
  if (!name) {
    return;
  }
  // quotes and apostrophes
  name = name.replace(/['"`]/g, '');

  // add dashes
  return _.kebabCase(name).toLowerCase();
}

export interface CommonRepoSelectProps {
  setProperties: (props: { repo?: string; create?: boolean }) => any;
  repo?: string;
  repos: { value: string; label: string }[] | null;
}

export const SimpleSelect: React.FC<CommonRepoSelectProps> = (props) => {
  return (
    <>
      <S.SelectLabel>Select a repository to use</S.SelectLabel>
      <SelectWidget
        onChange={(v) => {
          props.setProperties({
            create: false,
            repo: filterGitHubRepoName((v as any).value)
          });
        }}
        value={{
          value: props.repo || '',
          label: props.repo || ''
        }}
        placeholder="Select repository"
        options={props.repos || []}
        isLoading={!props.repos}
      />
      <S.Link>
        With GitHub user accounts you can only select existing GitHub repositories. If none of the listed repositories
        are suitable, create a blank repository for this app in GitHub and then return here.
      </S.Link>
    </>
  );
};

export const AdvancedSelect: React.FC<CommonRepoSelectProps & { appName: string; groupingName: string }> = (props) => {
  const [inputValue, setInputValue] = useState('');
  const isCustomRepo = (repo: string) => {
    return !props.repos?.find((r) => r.value === repo);
  };
  useEffect(() => {
    props.setProperties({
      repo: filterGitHubRepoName(props.appName),
      create: isCustomRepo(props.appName)
    });
  }, []);

  const repo = filterGitHubRepoName(props.repo);
  const isCustom = !props.repos?.find((r) => r.value === repo);
  const noExistingRepos = !props.repos || props.repos.length == 0;

  return (
    <>
      <S.SelectLabel>
        {isCustom
          ? `The below repo will automatically be created in the selected ${props.groupingName}.`
          : 'The existing repo below will be used.'}
      </S.SelectLabel>
      <SelectWidget
        onChange={(v) => {
          const selectedRepo = (v as any).value;
          props.setProperties({
            repo: filterGitHubRepoName(selectedRepo),
            create: isCustomRepo(selectedRepo)
          });
        }}
        value={{
          value: repo || '',
          label: repo || ''
        }}
        placeholder="Select repository"
        options={props.repos || []}
        isLoading={!props.repos}
        hideDropdown={noExistingRepos}
        defaultInputValue={repo}
        inputValue={inputValue}
        onBlur={() => {
          if (inputValue) {
            props.setProperties({
              repo: filterGitHubRepoName(inputValue),
              create: isCustomRepo(inputValue)
            });
          }
        }}
        onInputChange={(value) => {
          setInputValue(value);
        }}
        noOptionsMessage={(payload) => {
          if (!payload.inputValue) {
            return null;
          }
          return `Create new repo ${filterGitHubRepoName(payload.inputValue)}`;
        }}
      />
    </>
  );
};

export const GithubReposDropdownWidget: React.FC<GithubReposDropdownWidgetProps> = (props) => {
  const system = useSystem();
  const [repos, setRepos] = useState<{ value: string; label: string }[] | null>(null);
  const integrationProps = props.integration.getProperties<IntegrationKind.GitHub>();
  useEffect(() => {
    const client = system.clientProvisioner.provisionProviderClient();
    client
      .getGitHubRepositories({
        org_id: props.org.id,
        github_account: integrationProps.org
      })
      .then((c) => {
        setRepos(
          c.map((repo) => {
            return {
              value: repo.name,
              label: repo.name
            };
          })
        );
      })
      .finally(() => {});
  }, [integrationProps.org]);

  return (
    <S.SelectContainer padTop={true}>
      {integrationProps.account_type === GithubAccountType.User ? (
        <SimpleSelect {...props} repos={repos} />
      ) : (
        <AdvancedSelect {...props} repos={repos} />
      )}
    </S.SelectContainer>
  );
};

namespace S {
  export const SelectContainer = styled.div<{ padTop?: boolean }>`
    width: 100%;
    color: ${(p) => p.theme.text.primary};
    margin-top: ${(p) => (p.padTop ? 16 : 0)}px;
  `;

  export const SelectLabel = styled.div`
    color: ${(p) => p.theme.text.secondary};
    padding-bottom: 8px;
  `;

  export const Link = styled.p`
    padding-top: 16px;
    padding-bottom: 16px;
    color: ${(p) => p.theme.text.primary};
    font-size: 12px;
  `;
}
